import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  DialogContent,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, Globe, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type GetAvailableListingPortalsQuery,
  type PortalListingButtonFragment,
  Portals_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { ConfirmDialog } from '@modules/_shared/components/ConfirmDialog';
import { LoadingIconButton } from '@modules/_shared/components/LoadingIconButton';
import { LoadingSwitch } from '@modules/_shared/components/LoadingSwitch';
import { MutationErrorAlert } from '@modules/_shared/components/MutationErrorModal';
import { useTogglePortalListing } from '@modules/_shared/hooks/useTogglePortalListing';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { userCanPublishPortalListing } from '@modules/_shared/utils/listingPermissions';
import {
  GET_AVAILABLE_PORTALS,
  SYNC_PORTAL_LISTING,
} from '@modules/listings/lotsQueries';
import { isProduction } from '@shared/config';
import { useLocale } from '@shared/hooks/locale';
import { borderRadius } from '@shared/theme/StyleProvider';

interface ListingPortalsButtonProps {
  listing?: PortalListingButtonFragment | null;
  onPortalListingChange?: (portalId: string, live: boolean) => void;
  btnSize?: 'small' | 'medium' | 'large';
  readOnly?: boolean;
}

export const PORTAL_LISTINGS_BTN_FRAGMENT = gql(/* GraphQL */ `
  fragment PortalListingButton on lots {
    id
    broker {
      id
      teams_users {
        team_id
      }
    }
    portal_listings {
      id
      live
      portal_id
      portal {
        id
        name
        active
      }
    }
    enquiries {
      id
      created_at
      portal_id
    }
  }
`);

type Portal = GetAvailableListingPortalsQuery['portals'][number];

const PORTAL_PUBLISH_CONFIRMATION_KEY_PREFIX =
  'portal-publish-confirmation-accepted';

export const ListingPortalsList: React.FC<
  Omit<ListingPortalsButtonProps, 'isLoading'> & {
    fullWidth?: boolean;
    allowSynchronization?: boolean;
    allPortals?: GetAvailableListingPortalsQuery['portals'];
  }
> = ({
  listing,
  onPortalListingChange,
  allPortals,
  fullWidth = false,
  allowSynchronization = false,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    portalId: string;
    portalName: string;
    previousValue: boolean;
  }>({
    open: false,
    portalId: '',
    portalName: '',
    previousValue: false,
  });
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    togglePortalListing,
    WarningSnackbar,
    ErrorDialog,
    setToggleError,
    setToggleWarnings,
  } = useTogglePortalListing({ onPortalListingChange });

  const portals = useMemo(() => allPortals ?? [], [allPortals]);

  const performToggle = useCallback(
    async (portalId: string, previousValue: boolean) => {
      return togglePortalListing({
        variables: {
          portal_id: portalId,
          lot_id: listing?.id ?? '',
        },
      }).then(response => {
        // return checkbox state
        return response.data?.toggle_portal_listing?.live ?? previousValue;
      });
    },
    [listing, togglePortalListing],
  );

  const onTogglePortalState = useCallback(
    async (portalId: string, previousValue: boolean) => {
      const portal = portals.find(p => p.id === portalId);

      // Only show confirmation if:
      // 1. We're NOT in production environment
      // 2. Portal is Fotocasa or Comparis
      // 3. User hasn't previously confirmed
      if (
        !isProduction &&
        [
          Portals_Types_Enum_Enum.Fotocasa,
          Portals_Types_Enum_Enum.Comparis,
        ].includes(portal?.type as any) &&
        !previousValue && // Only show confirmation when publishing (not unpublishing)
        sessionStorage.getItem(
          `${PORTAL_PUBLISH_CONFIRMATION_KEY_PREFIX}-${portalId}`,
        ) !== 'true'
      ) {
        setConfirmationDialog({
          open: true,
          portalId,
          portalName: portal?.name ?? '',
          previousValue,
        });

        return previousValue;
      }

      return performToggle(portalId, previousValue);
    },
    [portals, performToggle],
  );

  const handleConfirmationSubmit = useCallback(async () => {
    setIsConfirming(true);
    try {
      sessionStorage.setItem(
        `${PORTAL_PUBLISH_CONFIRMATION_KEY_PREFIX}-${confirmationDialog.portalId}`,
        'true',
      );
      await performToggle(
        confirmationDialog.portalId,
        confirmationDialog.previousValue,
      );
      setConfirmationDialog(prev => ({ ...prev, open: false }));
    } finally {
      setIsConfirming(false);
    }
  }, [confirmationDialog, performToggle]);

  const handleConfirmationClose = useCallback(() => {
    if (!isConfirming) {
      setConfirmationDialog(prev => ({ ...prev, open: false }));
    }
  }, [isConfirming]);

  if (listing == null) {
    return null;
  }

  return (
    <>
      {portals.length === 0 ? (
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 3,
            width: fullWidth ? '100%' : '270px',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Globe size={20} />
          </Box>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('No portals available')}
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: allowSynchronization
              ? {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                }
              : { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            columnGap: { xs: 1, sm: 3 },
            rowGap: 0.25,
            py: 1,
            width: fullWidth ? '100%' : 'auto',
            maxWidth: 800,
          }}
        >
          {portals.map(portal => {
            const portalListing = listing.portal_listings.find(
              pl => pl.portal_id === portal.id,
            );

            const totalEnquiries = listing.enquiries.filter(
              enq => enq.portal_id === portal.id,
            ).length;

            const isSynchronizablePortal = [
              Portals_Types_Enum_Enum.Idealista,
              Portals_Types_Enum_Enum.Comparis,
              Portals_Types_Enum_Enum.Pisos,
              Portals_Types_Enum_Enum.Fotocasa,
            ].includes(portal.type);
            const isLive = portalListing?.live ?? false;

            return (
              <ListingPortalItem
                key={portal.id}
                portal={portal}
                isSynchronizable={
                  allowSynchronization && isSynchronizablePortal && !readOnly
                }
                isLive={isLive}
                totalEnquiries={totalEnquiries}
                listingId={listing.id}
                onTogglePortalState={onTogglePortalState}
                readOnly={readOnly}
                onSyncError={setToggleError}
                onSyncWarnings={setToggleWarnings}
              />
            );
          })}
        </Box>
      )}
      {ErrorDialog}
      {WarningSnackbar}
      <ConfirmDialog
        open={confirmationDialog.open}
        title={t('Confirm publication')}
        onClose={handleConfirmationClose}
        onSubmit={handleConfirmationSubmit}
        isSubmitting={isConfirming}
        okText={t('Yes, publish')}
        cancelText={t('Cancel')}
      >
        <DialogContent>
          <Typography>
            {t(
              'You are about to publish to {{portal}}, which uses a production URL. Are you sure you want to continue?',
              {
                portal: confirmationDialog.portalName,
              },
            )}
          </Typography>
        </DialogContent>
      </ConfirmDialog>
    </>
  );
};

type ListingPortalItemProps = {
  portal: Portal;
  isSynchronizable: boolean;
  isLive: boolean;
  totalEnquiries: number;
  listingId: string;
  onTogglePortalState: (
    portalId: string,
    previousValue: boolean,
  ) => Promise<boolean>;
  onSyncError: (error: ErrorLike) => void;
  onSyncWarnings: (warnings: any[]) => void;
  readOnly?: boolean;
};

const ListingPortalItem: React.FC<ListingPortalItemProps> = ({
  portal,
  isSynchronizable,
  isLive,
  totalEnquiries,
  listingId,
  onTogglePortalState,
  onSyncError,
  onSyncWarnings,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const { me } = useAppData();

  const [syncListingPortals] = useMutation(SYNC_PORTAL_LISTING, {
    onError: e => onSyncError(e),
    onCompleted: onCompletedData => {
      if (onCompletedData.update_portal_listing?.warnings != null) {
        onSyncWarnings(onCompletedData.update_portal_listing.warnings);
      }
    },
  });

  const handleToggle = useCallback(
    (nextValue: boolean) => onTogglePortalState(portal.id, !nextValue),
    [portal.id, onTogglePortalState],
  );

  const canPublish = readOnly
    ? false
    : (me?.is_admin ?? false) ||
      userCanPublishPortalListing(me, portal, isLive);

  const secondaryAction = useMemo(() => {
    if (isSynchronizable && isLive && !readOnly) {
      return (
        <Tooltip
          title={t('Sync listing to {{portal}}', {
            portal: portal.name ?? portal.type,
          })}
        >
          <LoadingIconButton
            size="small"
            disabled={!canPublish}
            onClick={async e => {
              e.stopPropagation();
              return syncListingPortals({
                variables: { portalId: portal.id, lotId: listingId },
              });
            }}
          >
            <RefreshCw size={16} />
          </LoadingIconButton>
        </Tooltip>
      );
    }

    return null;
  }, [
    isSynchronizable,
    isLive,
    listingId,
    portal.id,
    syncListingPortals,
    portal.name,
    portal.type,
    canPublish,
    readOnly,
    t,
  ]);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: '8px',
        breakInside: 'avoid',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <LoadingSwitch
          checked={isLive}
          disabled={!canPublish}
          onClick={handleToggle}
          size="small"
          inputAriaLabel={portal.name ?? portal.type}
        />
      </Box>
      <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
        {me?.is_admin ? (
          <Typography
            component={Link}
            to={`/settings/portals/${portal.id}`}
            target="_blank"
            noWrap
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {portal.name}
          </Typography>
        ) : (
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {portal.name}
          </Typography>
        )}
        <Typography
          noWrap
          sx={{
            fontSize: '0.75rem',
            color: totalEnquiries > 0 ? 'text.secondary' : 'text.disabled',
          }}
        >
          {t('{{count}} enquiries', { count: totalEnquiries })}
        </Typography>
      </Stack>
      {secondaryAction != null && (
        <Box sx={{ flexShrink: 0 }}>{secondaryAction}</Box>
      )}
    </Stack>
  );
};

export const ListingPortalsButton: React.FC<
  ListingPortalsButtonProps
> = props => {
  const { listing, btnSize, ...listProps } = props;

  const { data, loading, error } = useQuery(GET_AVAILABLE_PORTALS, {
    variables: {
      lot_id: listing?.id ?? '',
      team_ids: listing?.broker?.teams_users.map(tu => tu.team_id) ?? [],
    },
    skip: listing == null,
  });

  const { t } = useLocale();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const livePortals = listing?.portal_listings.filter(pl => pl.live) ?? [];
  const nbLivePortals = livePortals.length ?? 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'portals-popover' : undefined;

  return (
    <>
      {loading ? (
        <Skeleton variant="rounded" width={100}>
          <Button size={btnSize} endIcon={<ChevronDown size={16} />}>
            placeholder
          </Button>
        </Skeleton>
      ) : (
        <Button
          size={btnSize}
          onClick={handleClick}
          aria-describedby={id}
          sx={theme => ({
            height: 32,
            px: 1.5,
            fontSize: '0.875rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: 'text.primary',
            border: `1px solid ${(theme.vars ?? theme).palette.grey[300]}`,
            backgroundColor: (theme.vars ?? theme).palette.background.paper,
            borderRadius: borderRadius.medium,
            '&:hover': {
              borderColor: (theme.vars ?? theme).palette.grey[400],
              backgroundColor: (theme.vars ?? theme).palette.grey[50],
            },
            '& .MuiButton-startIcon': { mr: 0.75 },
            '& .MuiButton-endIcon': { ml: 1 },
          })}
          startIcon={
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: nbLivePortals > 0 ? 'success.main' : 'text.disabled',
              }}
            />
          }
          endIcon={<ChevronDown size={16} />}
        >
          {nbLivePortals > 0
            ? t('Online {{count}}', { count: nbLivePortals })
            : t('Offline')}
        </Button>
      )}
      {listing != null && !loading && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          sx={{ '& .MuiPopover-paper': { px: 1.5, py: 0.5, mt: 1 } }}
        >
          {error != null ? (
            <MutationErrorAlert error={error} />
          ) : (
            <ListingPortalsList
              listing={listing}
              {...listProps}
              allPortals={data?.portals}
            />
          )}
        </Popover>
      )}
    </>
  );
};
